import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { useEngagements } from '../lib/queries'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDate, thaiDateTime } from '../lib/format'

/**
 * AI-assisted audit report narrative (part 51).
 *
 * A narrative summarises a period's deterministic findings. It is not a verdict and it cannot
 * change a finding. This page can generate a draft, read the aggregate snapshot behind it, and
 * record a human accept/reject decision. There is no publish control: publication stays a manual
 * step outside this screen.
 *
 * A row whose model is "template" was built deterministically in Go because AI_BASE_URL was not
 * configured. It is labelled as a template everywhere; the UI never presents it as model output.
 */

type NarrativeStatus = 'draft' | 'accepted' | 'rejected'
type Decision = 'accepted' | 'rejected'

interface NarrativeRow {
  id: string
  engagement_id: string
  engagement_code?: string
  engagement_title?: string
  client_name?: string
  period_start: string
  period_end: string
  model: string
  prompt_version: string
  prompt_tokens: number
  completion_tokens: number
  narrative: string
  findings_total: number
  findings_open: number
  findings_critical: number
  findings_high: number
  findings_medium: number
  findings_low: number
  evidence_count: number
  amount_at_risk_satang: number
  status: NarrativeStatus
  reviewed_by?: string
  reviewed_by_name?: string
  reviewed_at?: string
  created_at: string
}

const statusLabel: Record<NarrativeStatus, string> = {
  draft: 'รอตรวจ',
  accepted: 'ยอมรับแล้ว',
  rejected: 'ตีกลับแล้ว',
}

function shortID(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id
}

export function Narratives() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canGenerate = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager' || me?.role === 'auditor'
  const canReview = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager' || me?.role === 'reviewer'

  const engagements = useEngagements()
  const [engagementId, setEngagementId] = useState('')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [status, setStatus] = useState<NarrativeStatus | 'all'>('all')
  const [openId, setOpenId] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<{ id: string; decision: Decision } | null>(null)
  const [notice, setNotice] = useState('')

  const allNarratives = useQuery({
    queryKey: ['report-narratives', 'all'],
    queryFn: () => api<NarrativeRow[]>('/v1/narratives'),
    staleTime: 10_000,
  })
  const filtered = useQuery({
    queryKey: ['report-narratives', status],
    queryFn: () => api<NarrativeRow[]>(`/v1/narratives${status === 'all' ? '' : `?status=${status}`}`),
    enabled: status !== 'all',
    staleTime: 10_000,
  })

  const selectedEngagement = useMemo(
    () => (engagements.data ?? []).find((e) => e.id === engagementId),
    [engagements.data, engagementId],
  )

  const chooseEngagement = (id: string) => {
    setEngagementId(id)
    setNotice('')
    const e = (engagements.data ?? []).find((row) => row.id === id)
    if (e) {
      setPeriodStart(e.period_start)
      setPeriodEnd(e.period_end)
    }
  }

  const useEngagementPeriod = () => {
    if (!selectedEngagement) return
    setPeriodStart(selectedEngagement.period_start)
    setPeriodEnd(selectedEngagement.period_end)
  }

  const generate = useMutation({
    mutationFn: () => apiWrite<NarrativeRow>('/v1/narratives', 'POST', {
      engagement_id: engagementId,
      period_start: periodStart,
      period_end: periodEnd,
    }),
    onSuccess: (row) => {
      setOpenId(row.id)
      setNotice(row.model === 'template'
        ? 'สร้างคำบรรยายจากแม่แบบแล้ว — ระบบไม่ได้เรียกโมเดลภายนอก'
        : `สร้างคำบรรยายด้วย ${row.model} แล้ว — ต้องตรวจทานก่อนใช้ทุกครั้ง`)
      void qc.invalidateQueries({ queryKey: ['report-narratives'] })
    },
  })

  const review = useMutation({
    mutationFn: (input: { id: string; decision: Decision }) =>
      apiWrite<NarrativeRow>(`/v1/narratives/${input.id}/review`, 'POST', { decision: input.decision }),
    onSuccess: (row) => {
      setConfirm(null)
      setOpenId(row.id)
      setNotice(row.status === 'accepted'
        ? 'บันทึกการยอมรับคำบรรยายแล้ว — สถานะข้อสังเกตไม่เปลี่ยนแปลง'
        : 'บันทึกการตีกลับคำบรรยายแล้ว — ระบบไม่แก้ไขข้อสังเกตให้')
      void qc.invalidateQueries({ queryKey: ['report-narratives'] })
    },
  })

  const rows = status === 'all' ? (allNarratives.data ?? []) : (filtered.data ?? [])
  const loading = allNarratives.isLoading || (status !== 'all' && filtered.isLoading)
  const loadError = allNarratives.error ?? filtered.error
  const refreshing = allNarratives.isFetching || (status !== 'all' && filtered.isFetching)

  const counts = useMemo(() => {
    const out = { draft: 0, accepted: 0, rejected: 0 }
    for (const row of allNarratives.data ?? []) out[row.status] += 1
    return out
  }, [allNarratives.data])

  const refetchAll = () => {
    void allNarratives.refetch()
    if (status !== 'all') void filtered.refetch()
  }

  return (
    <>
      <PageHead
        title="คำบรรยายรายงานการตรวจสอบโดย AI"
        sub="AI ช่วยสรุปภาพรวมของรอบระยะเวลาจากข้อสังเกตที่กฎตัดสินแล้ว — มนุษย์เป็นผู้อ่านและผู้อนุมัติ"
        actions={
          <button className="btn btn-ghost btn-sm" onClick={refetchAll} disabled={refreshing}>
            {refreshing ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      <div
        className="card card-pad"
        style={{
          marginBottom: 18,
          borderColor: 'rgba(200, 169, 106, 0.35)',
          background: 'linear-gradient(180deg, rgba(200, 169, 106, 0.07), transparent)',
        }}
      >
        <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
          <span className="chip gold">การ์ดเรล</span>
          <div>
            <div style={{ fontSize: 13.5, lineHeight: 1.75 }}>
              AI เป็นเพียงผู้ช่วยร่างคำบรรยายภาพรวม ไม่มีสิทธิ์สรุปว่าผู้ใดกระทำผิด
              และไม่สามารถเปลี่ยนสถานะของข้อสังเกตได้
            </div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6 }}>
              คำบรรยายที่ใช้ถ้อยคำตัดสินหรือไม่มีประโยคยืนยันการตรวจสอบโดยมนุษย์จะถูกปฏิเสธก่อนบันทึก
              การยอมรับในหน้านี้คือการรับรองถ้อยคำสำหรับรายงาน ไม่ใช่การอนุมัติผลการตรวจสอบ
            </div>
          </div>
        </div>
      </div>

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <Stat label="คำบรรยายทั้งหมด" value={rows.length} unit="รายการ" foot="ตามตัวกรองที่เลือก" />
        <Stat label="รอตรวจ" value={counts.draft} unit="รายการ" foot="ยังไม่มีผู้ลงชื่อ" />
        <Stat label="ยอมรับแล้ว" value={counts.accepted} unit="รายการ" foot="ถ้อยคำผ่านการตรวจ" />
        <Stat label="ตีกลับแล้ว" value={counts.rejected} unit="รายการ" foot="ไม่นำถ้อยคำไปใช้" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 20 }}>
        <div className="card-label">สร้างคำบรรยายสำหรับรอบระยะเวลา</div>
        <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 10 }}>
          เลือกโครงการและรอบระยะเวลาที่ต้องการสรุป ระบบจะนับข้อสังเกตที่เกิดขึ้นในช่วงนั้น
          และบันทึกตัวเลขสรุปไว้คู่กับคำบรรยาย เพื่อให้ตรวจย้อนกลับได้ว่าผู้เขียนเห็นข้อมูลใด
          หากยังไม่ได้ตั้งค่าผู้ให้บริการ AI ระบบจะสร้างจากแม่แบบในเครื่องและระบุว่าเป็นแม่แบบอย่างชัดเจน
        </p>

        <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label htmlFor="narrative-engagement" className="dim" style={{ fontSize: 12 }}>
            โครงการ
            <select
              id="narrative-engagement"
              className="select"
              style={{ display: 'block', minWidth: 320, maxWidth: '100%', marginTop: 6 }}
              value={engagementId}
              onChange={(e) => chooseEngagement(e.target.value)}
              disabled={!canGenerate || engagements.isLoading}
            >
              <option value="">— เลือกโครงการ —</option>
              {(engagements.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>{e.code} · {e.title}</option>
              ))}
            </select>
          </label>

          <label htmlFor="narrative-start" className="dim" style={{ fontSize: 12 }}>
            เริ่มรอบ
            <input
              id="narrative-start"
              className="input"
              type="date"
              style={{ display: 'block', marginTop: 6 }}
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              disabled={!canGenerate}
            />
          </label>

          <label htmlFor="narrative-end" className="dim" style={{ fontSize: 12 }}>
            สิ้นสุดรอบ
            <input
              id="narrative-end"
              className="input"
              type="date"
              style={{ display: 'block', marginTop: 6 }}
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              disabled={!canGenerate}
            />
          </label>

          <button
            className="btn btn-ghost btn-sm"
            onClick={useEngagementPeriod}
            disabled={!canGenerate || !selectedEngagement}
          >
            ใช้รอบของโครงการ
          </button>
          <button
            className="btn btn-primary"
            disabled={!canGenerate || engagementId === '' || periodStart === '' || periodEnd === '' || generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? 'กำลังสร้างคำบรรยาย…' : 'สร้างคำบรรยาย'}
          </button>
        </div>

        {selectedEngagement ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.7 }}>
            {selectedEngagement.title} · รอบโครงการ {thaiDate(selectedEngagement.period_start)} — {thaiDate(selectedEngagement.period_end)}
          </div>
        ) : null}

        {!canGenerate ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            บทบาทของคุณอ่านคำบรรยายได้ แต่ไม่มีสิทธิ์สร้างฉบับใหม่
          </div>
        ) : null}

        {generate.isError ? (
          <div className="form-error">
            {generate.error instanceof Error ? generate.error.message : 'สร้างคำบรรยายไม่สำเร็จ'}
          </div>
        ) : null}

        {notice ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12, color: 'var(--color-gold)', lineHeight: 1.7 }}>
            {notice}
          </div>
        ) : null}
      </div>

      <div className="filters">
        <label htmlFor="narrative-status" className="dim" style={{ fontSize: 12 }}>สถานะ</label>
        <select
          id="narrative-status"
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value as NarrativeStatus | 'all')}
        >
          <option value="all">ทั้งหมด</option>
          <option value="draft">รอตรวจ</option>
          <option value="accepted">ยอมรับแล้ว</option>
          <option value="rejected">ตีกลับแล้ว</option>
        </select>
        <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
      </div>

      {loading ? <Skel h={220} /> : loadError ? (
        <div className="form-error">
          {loadError instanceof Error ? loadError.message : 'โหลดคำบรรยายไม่สำเร็จ'}
        </div>
      ) : rows.length === 0 ? (
        <Empty
          title="ยังไม่มีคำบรรยาย"
          body="เลือกโครงการและรอบระยะเวลาด้านบนแล้วกด “สร้างคำบรรยาย” ฉบับร่างจะยังไม่ถูกนำไปใช้จนกว่ามนุษย์จะยอมรับ"
        />
      ) : (
        <div className="table-wrap">
          <table className="table data">
            <thead>
              <tr>
                <th>สร้างเมื่อ</th>
                <th>โครงการและรอบ</th>
                <th>โมเดล</th>
                <th className="num">ข้อสังเกต</th>
                <th className="num">มูลค่าความเสี่ยง</th>
                <th>สถานะ</th>
                <th>ผู้ตรวจ</th>
                <th>คำบรรยาย</th>
                <th style={{ textAlign: 'right' }}>การตัดสิน</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = openId === row.id
                const deciding = confirm?.id === row.id
                return (
                  <Fragment key={row.id}>
                    <tr style={{ cursor: 'default' }}>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.created_at)}</td>
                      <td>
                        <div>{row.engagement_title || row.engagement_code || 'ไม่พบชื่อโครงการ'}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {thaiDate(row.period_start)} — {thaiDate(row.period_end)}
                        </div>
                      </td>
                      <td>
                        {row.model === 'template' ? (
                          <span className="chip" title="สร้างจากแม่แบบในเครื่อง ไม่ได้เรียกโมเดล">
                            แม่แบบในระบบ
                          </span>
                        ) : (
                          <span className="chip gold" title="สร้างโดยผู้ให้บริการโมเดลที่ตั้งค่าไว้">
                            {row.model}
                          </span>
                        )}
                      </td>
                      <td className="num">
                        <div>{number(row.findings_total)}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          เปิด {number(row.findings_open)} · วิกฤต {number(row.findings_critical)} · สูง {number(row.findings_high)}
                        </div>
                      </td>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>{money(row.amount_at_risk_satang)}</td>
                      <td>
                        <span
                          className="chip"
                          style={row.status === 'accepted'
                            ? { color: 'var(--color-gold)', borderColor: 'rgba(200,169,106,0.35)' }
                            : row.status === 'rejected' ? { color: '#E3A0A9' } : undefined}
                        >
                          {statusLabel[row.status]}
                        </span>
                      </td>
                      <td>
                        {row.reviewed_at ? (
                          <>
                            <div>{row.reviewed_by_name || 'ผู้ใช้ที่ถูกลบ'}</div>
                            <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{thaiDateTime(row.reviewed_at)}</div>
                          </>
                        ) : (
                          <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่ตรวจ</span>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(isOpen ? null : row.id)}>
                          {isOpen ? 'ซ่อน' : 'อ่าน'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.status !== 'draft' ? (
                          <span className="dim" style={{ fontSize: 11.5 }}>ตัดสินแล้ว</span>
                        ) : !canReview ? (
                          <span className="dim" style={{ fontSize: 11.5 }}>รอผู้มีสิทธิ์ตรวจ</span>
                        ) : deciding ? (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <span className="dim" style={{ fontSize: 11 }}>
                              {confirm?.decision === 'accepted' ? 'ยืนยันการยอมรับ?' : 'ยืนยันการตีกลับ?'}
                            </span>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={review.isPending}
                              onClick={() => confirm && review.mutate(confirm)}
                            >
                              {review.isPending ? 'กำลังบันทึก…' : 'ยืนยัน'}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)}>ยกเลิก</button>
                          </span>
                        ) : (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setConfirm({ id: row.id, decision: 'accepted' })}
                            >
                              ยอมรับ
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#E3A0A9' }}
                              onClick={() => setConfirm({ id: row.id, decision: 'rejected' })}
                            >
                              ตีกลับ
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr>
                        <td colSpan={9} style={{ background: 'var(--color-wash)', cursor: 'default' }}>
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, fontSize: 13 }}>
                            {row.narrative}
                          </div>
                          <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 12, borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                            ตัวเลขที่ใช้ร่าง: ข้อสังเกต {number(row.findings_total)} รายการ ·
                            หลักฐาน {number(row.evidence_count)} รายการ ·
                            โทเคน {number(row.prompt_tokens)} / {number(row.completion_tokens)} ·
                            รหัสฉบับ {row.prompt_version} · อ้างอิง {shortID(row.id)}
                          </div>
                          <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 6 }}>
                            คำบรรยายนี้ไม่มีผลต่อสถานะข้อสังเกต และยังไม่ถือว่าเผยแพร่แล้ว
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {review.isError ? (
        <div className="form-error">
          {review.error instanceof Error ? review.error.message : 'บันทึกการตัดสินไม่สำเร็จ'}
        </div>
      ) : null}
    </>
  )
}
